import React, { useState, useContext, useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation, useParams } from "react-router-dom";
import _ from "lodash";
import moment from "moment";

import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Typography,
  Collapse,
  LinearProgress,
} from "@material-ui/core";
import API from "../../API";

import { FleetContext, FleetSettingsContext } from "../../context";
import { FilledSelect } from "../../components/FormElements";
import { GeofencesExportOptions } from "../../constants";
import { withSnackbar } from "../../HOC/withSnackbar";
import { errorMessageBuilder } from "../../util/helpers";

export const parseError = (err) => {
  if (err.response && err.response.data) {
    if (err.response.data.error) {
      return err.response.data.error;
    }
    if (err.response.data instanceof Array) {
      let message = "";
      err.response.data.forEach((singleErr) => {
        if (singleErr instanceof Object) {
          // remove big whitespaces
          message += `${singleErr.error.replace(/\s+/g, " ")} `;
        } else {
          message += `${singleErr} `;
        }
      });
      return message;
    }
  }
  return err.message;
};

export const saveFileToDisk = (content, filename, mimeType) => {
  const blob = new Blob([content], { type: mimeType });
  const element = document.createElement("a");
  const URL = window.URL || window.webkitURL;
  const downloadURI = URL.createObjectURL(blob);
  element.href = downloadURI;
  element.setAttribute("download", filename, mimeType);
  element.style.display = "none";
  document.body.appendChild(element);
  element.click();
  document.body.removeChild(element);
  URL.revokeObjectURL(element.href);
};

const ExportJSON = ({ setSnackbarMessage, setAlertSeverity, fileTypes }) => {
  const { FleetSpecificJSON, FleetAgnosticJSON, GeofenceBinaryPreview, GeofenceBinaryUpload } =
    GeofencesExportOptions;
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(true);
  const [fileType, setFileType] = useState(fileTypes[0]);
  const [isLoading, setIsLoading] = useState(false);
  const [failure, setFailure] = useState(null);
  const [version, setVersion] = useState("");
  const history = useHistory();
  const location = useLocation();
  const { activeFleet } = useContext(FleetContext);
  const { fetchGeofencePackages, geofencePackages } = useContext(FleetSettingsContext);
  const { key, name, layers } = location.state;
  const { fleetId } = useParams();

  useEffect(() => {
    (async () => {
      const market = activeFleet.market?.split("/").pop();
      const geofencePackagesParams = market ? { market } : {};
      await fetchGeofencePackages(geofencePackagesParams);
    })();
  }, []);

  useEffect(() => {
    try {
      if (!version) {
        const versionArray = geofencePackages[0].version.split("+")[0].split(".");
        const minor = parseInt(versionArray[1], 10);
        versionArray[1] = minor + 1;
        versionArray[2] = 0;
        setVersion(versionArray.join("."));
      }
    } catch (error) {
      // empty
    }
  }, [geofencePackages]);

  const makeAgnostic = (obj, keysToDelete, valuesToNull) => {
    // Removes fleet specific data from export all throughout nested objects

    const objVals = Object.values(obj);
    objVals.forEach((objVal) => {
      if (objVal.type === "parking_incentive_zone") {
        objVal.type = "allow_parking_zone";
      }
      _.set(objVal, "incentive", {});
      const zones = Object.values(objVal.zones);
      zones.forEach((zone) => {
        if (zone.rules && zone.rules.length !== 0) {
          zone.rules.length = 1;
        }
        if (zone.rules[0].label === "parking_incentive_zone") {
          zone.rules[0].label = "allow_parking_zone";
        }
        keysToDelete.forEach((keyToDel) => {
          _.unset(zone, keyToDel);
          _.unset(zone.rules[0], keyToDel);
        });
        valuesToNull.forEach((valToNull) => {
          _.set(zone, valToNull, null);
          _.set(zone.rules[0], valToNull, null);
        });
      });
    });
  };

  const makeGeo = (obj) => {
    const collectionFormat = {
      type: "FeatureCollection",
      features: [],
    };
    const featureFormat = {
      type: "Feature",
      geometry: {
        type: "Polygon",
      },
    };
    const objVals = Object.values(obj);
    objVals.forEach((objVal) => {
      const zones = Object.values(objVal.zones);
      zones.forEach((zone) => {
        const newFeature = _.cloneDeep(featureFormat);
        newFeature.properties = {
          hidden: objVal.hidden,
          incentive: objVal.incentive,
          layerKey: objVal.key,
          name: objVal.name,
          speed: objVal.speed,
        };
        const { geometry, properties } = newFeature;
        geometry.zone_type = objVal.type;
        geometry.name = zone.name;
        geometry.coordinates = zone.polygon;
        geometry.coordinates?.forEach((coordArray) =>
          coordArray.forEach((coordElem) => coordElem.reverse()),
        );
        const zoneKeys = Object.keys(zone);
        zoneKeys.forEach((zoneKey) => {
          if (zoneKey !== "polygon") {
            properties[zoneKey] = zone[zoneKey];
          }
        });
        collectionFormat.features.push(newFeature);
      });
    });
    return collectionFormat;
  };

  const downloadFile = async (type, fileExtension, exportLayers = null) => {
    let fileName;
    let content;
    let mimeType;
    try {
      if (type === GeofenceBinaryPreview) {
        const date = moment().format("YYYYMMDDTHHmm");
        fileName = `${activeFleet.canonical_name}_binaries_${date}`;
        content = await API.geofenceApi.getGeofenceBinaries(fleetId);
        mimeType = "application/x-zip-compressed";
      } else {
        fileName = key && name.value ? name.value : activeFleet.canonical_name;
        content = JSON.stringify(
          fileExtension === "geojson" ? makeGeo(exportLayers) : { name, layers: exportLayers },
        );
        mimeType = "text/plain";
      }
      saveFileToDisk(content, `${fileName}.${fileExtension}`, mimeType);
    } catch (error) {
      console.error(error);
      setFailure(parseError(error));
      const errorMessage = errorMessageBuilder(
        t("Fleet.Geofences.ExportModal.DownloadRequestFailure"),
        error.response.status,
        error.message,
      );
      setSnackbarMessage(errorMessage);
      setAlertSeverity("error");
    }
  };

  const exportGeofencesData = async () => {
    let extension = "json";
    const exportLayers = _.cloneDeep(layers);
    const keysToDelete = ["id", "ruleId", "schedule", "market", "url"];
    const valuesToNull = ["next_rule", "week_days", "month_days", "months", "parking_incentive"];

    try {
      switch (fileType) {
        case FleetSpecificJSON:
          break;
        case FleetAgnosticJSON:
          makeAgnostic(exportLayers, keysToDelete, valuesToNull);
          break;
        case GeofenceBinaryPreview:
          extension = "zip";
          break;
        default:
          extension = "geojson";
      }
      if (fileType !== GeofenceBinaryUpload && fileType !== GeofenceBinaryPreview)
        await downloadFile(fileType, extension, exportLayers);
    } catch (error) {
      console.error(error);
      setFailure(parseError(error));
    }
  };

  const handleClose = (e = null) => {
    if (e) e.stopPropagation();
    setIsOpen(false);
    history.goBack();
  };

  const handleSubmit = async () => {
    setIsLoading(true);

    try {
      await exportGeofencesData();
      if (fileType === GeofenceBinaryUpload) {
        history.push(
          `${history.location.pathname.split("/").slice(0, -1).join("/")}/create-package`,
        );
      } else if (fileType === GeofenceBinaryPreview) {
        history.push(
          `${history.location.pathname.split("/").slice(0, -1).join("/")}/create-preview-package`,
        );
      } else {
        handleClose();
      }
    } catch (err) {
      console.error(err);
      setFailure(parseError(err));
    }
    setIsLoading(false);
  };

  return (
    <>
      <Dialog open={isOpen} onClose={handleClose}>
        <DialogTitle>{t("Fleet.Geofences.ExportModal.Title")}</DialogTitle>
        <DialogContent style={{ width: "300px" }}>
          <FilledSelect
            label={t("Fleet.Geofences.ExportModal.Label")}
            value={fileType}
            onChange={(e) => setFileType(e.target.value)}
            FormControlProps={{ fullWidth: true, margin: "normal" }}
          >
            {fileTypes.map(
              (ft) =>
                ft && (
                  <MenuItem value={ft} key={ft}>
                    {t(`Fleet.Geofences.ExportModal.${ft}`)}
                  </MenuItem>
                ),
            )}
          </FilledSelect>
          <Collapse in={isLoading} timeout={1000}>
            <LinearProgress color="secondary" />
          </Collapse>
          <Collapse in={!!failure} timeout={1000}>
            <Typography color="error">{failure}</Typography>
          </Collapse>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{t("Fleet.Geofences.ExportModal.Cancel")}</Button>
          <Button onClick={handleSubmit}>{t("Fleet.Geofences.ExportModal.Export")}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

ExportJSON.propTypes = {
  setSnackbarMessage: PropTypes.func.isRequired,
  setAlertSeverity: PropTypes.func.isRequired,
  fileTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default withSnackbar(ExportJSON);
